/**
 * Custom Measurements JS
 * Updated to work with div-based accordion (no <details>/<summary>)
 * and opacity/visibility fade system (no display:none)
 */

document.addEventListener("DOMContentLoaded", function () {

  const CustomMeasurements = {

    currentUnit: "cm",
    initialized: false,
    lastConversionTime: 0,
    elements: {},

    init: function () {
      if (this.initialized) return;
      this.initialized = true;
      this.refreshElements();

      if (this.elements.container) {
        this.storeInitialValues();
        this.setupUnitToggle();
        this.setupNumberValidation();
        this.setupMeasuringGuideModal();
        // this.setupOptionGroups(); // DISABLED 2026-07-25: inline script in custom-measurements.liquid already handles the option accordion; running both cancels the open/close
        this.setupResetButton();
        console.log("CustomMeasurements: Initialization complete");
      }
    },

    refreshElements: function () {
      const container = document.querySelector(".cm-container");
      if (container) {
        this.elements = {
          container:         container,
          optionGroups:      container.querySelectorAll(".cm-option-group"),
          unitButtons:       container.querySelectorAll(".cm-unit-btn"),
          resetButton:       container.querySelector(".cm-reset-btn"),
          measurementInputs: container.querySelectorAll(".cm-custom-input"),
          unitLabels:        container.querySelectorAll(".field__unit-text"),
          modalButton:       container.querySelector("#cm-modal-button, .cm-guide-btn"),
          selectedOptionField: document.getElementById("cm-selected-option"),
          oldModal:          document.getElementById("measuring-modal"),
          newModal:          document.getElementById("measuring-modal-v2"),
        };
      } else {
        this.elements = {};
      }
    },

    storeInitialValues: function () {
      const { measurementInputs } = this.elements;
      if (!measurementInputs || measurementInputs.length === 0) return;
      measurementInputs.forEach((input) => {
        if (!input.hasAttribute("data-original-value")) {
          input.setAttribute("data-original-value", input.value || "");
          input.setAttribute("data-original-unit", this.currentUnit);
        }
      });
    },

    // ── Conversion helpers ─────────────────────────────────────────────────
    cmToMm:  (v) => Number(v) * 10,
    cmToIn:  (v) => Number(v) * 0.3937008,
    mmToCm:  (v) => Number(v) * 0.1,
    mmToIn:  (v) => Number(v) * 0.0393701,
    inToCm:  (v) => Number(v) * 2.54,
    inToMm:  (v) => Number(v) * 25.4,

    convertValue: function (value, fromUnit, toUnit) {
      if (value === null || value === undefined || value === "") return "";
      const numValue = Number(value);
      if (isNaN(numValue)) return value;
      if (fromUnit === toUnit) return numValue;
      if (fromUnit === "cm" && toUnit === "mm") return this.cmToMm(numValue);
      if (fromUnit === "cm" && toUnit === "in") return this.cmToIn(numValue);
      if (fromUnit === "mm" && toUnit === "cm") return this.mmToCm(numValue);
      if (fromUnit === "mm" && toUnit === "in") return this.mmToIn(numValue);
      if (fromUnit === "in" && toUnit === "cm") return this.inToCm(numValue);
      if (fromUnit === "in" && toUnit === "mm") return this.inToMm(numValue);
      return numValue;
    },

    formatValue: function (value, unit) {
      if (value === null || value === undefined || value === "") return "";
      const numValue = Number(value);
      if (isNaN(numValue)) return value;
      if (Math.floor(numValue) === numValue) return numValue.toString();
      if (unit === "in") return numValue.toFixed(2);
      if (numValue < 1) return numValue.toFixed(2);
      return numValue.toFixed(1);
    },

    // ── Unit toggle ────────────────────────────────────────────────────────
    setupUnitToggle: function () {
      const { unitButtons } = this.elements;
      if (!unitButtons || unitButtons.length === 0) return;

      unitButtons.forEach((button) => {
        const newButton = button.cloneNode(true);
        button.parentNode.replaceChild(newButton, button);
      });

      this.elements.unitButtons = this.elements.container.querySelectorAll(".cm-unit-btn");
      const freshButtons = this.elements.unitButtons;
      const self = this;

      freshButtons.forEach((button) => {
        const unitType = button.getAttribute("data-unit") || button.textContent.trim().toLowerCase();
        if (!["cm", "mm", "in"].includes(unitType)) return;
        if (!button.getAttribute("data-unit")) button.setAttribute("data-unit", unitType);

        button.addEventListener("click", function (event) {
          event.preventDefault();
          if (self.currentUnit === unitType) return;

          const now = Date.now();
          if (now - self.lastConversionTime < 300) return;
          self.lastConversionTime = now;

          const oldUnit = self.currentUnit;
          self.currentUnit = unitType;

          freshButtons.forEach((btn) => btn.classList.remove("active"));
          this.classList.add("active");

          if (self.elements.unitLabels) {
            self.elements.unitLabels.forEach((label) => { label.textContent = unitType; });
          }

          if (self.elements.measurementInputs) {
            self.elements.measurementInputs.forEach((input) => {
              const currentValue = input.value;
              if (currentValue && currentValue.trim() !== "") {
                if (oldUnit === "cm" && unitType === "mm") {
                  const numValue = Number(currentValue);
                  if (!isNaN(numValue)) {
                    const result = numValue * 10;
                    input.value = Math.floor(result) === result ? result.toString() : result.toFixed(1);
                  }
                } else {
                  const originalValue = input.getAttribute("data-original-value");
                  const originalUnit  = input.getAttribute("data-original-unit");
                  if (originalValue && originalUnit) {
                    const numOriginal = Number(originalValue);
                    if (!isNaN(numOriginal)) {
                      input.value = self.formatValue(self.convertValue(numOriginal, originalUnit, unitType), unitType);
                    }
                  } else {
                    const numValue = Number(currentValue);
                    if (!isNaN(numValue)) {
                      input.value = self.formatValue(self.convertValue(numValue, oldUnit, unitType), unitType);
                    }
                  }
                }
              }

              if (input.name && input.name.includes("properties[")) {
                const propertyName = input.name.split("[")[1].split("(")[0].trim();
                input.name = `properties[${propertyName} (${unitType})]`;
              }
            });
          }
        });
      });
    },

    // ── Reset button ───────────────────────────────────────────────────────
    // ── Numeric-only input sanitising ────────────────────────
    setupNumberValidation: function () {
      var inputs = this.elements.measurementInputs;
      if (!inputs || inputs.length === 0) return;
      function clean(val) {
        var c = String(val).replace(/[^0-9.]/g, '');
        var p = c.split('.');
        if (p.length > 2) c = p[0] + '.' + p.slice(1).join('');
        return c;
      }
      inputs.forEach(function (input) {
        input.setAttribute('inputmode', 'decimal');
        input.addEventListener('keypress', function (e) {
          if (e.key && e.key.length === 1 && !/[0-9.]/.test(e.key)) e.preventDefault();
        });
        input.addEventListener('input', function () {
          var c = clean(this.value);
          if (this.value !== c) this.value = c;
        });
        input.addEventListener('paste', function () {
          var self = this;
          setTimeout(function () { self.value = clean(self.value); }, 0);
        });
      });
    },

    setupResetButton: function () {
      const { resetButton } = this.elements;
      if (!resetButton) return;

      const self = this;
      const newResetButton = resetButton.cloneNode(true);
      resetButton.parentNode.replaceChild(newResetButton, resetButton);
      this.elements.resetButton = newResetButton;

      newResetButton.addEventListener("click", function (event) {
        event.preventDefault();
        if (self.elements.measurementInputs) {
          self.elements.measurementInputs.forEach((input) => { input.value = ""; });
        }
      });
    },

    // ── Measuring guide modal ──────────────────────────────────────────────
    setupMeasuringGuideModal: function () {
      const { modalButton, oldModal, newModal } = this.elements;
      if (!modalButton) return;

      const newModalButton = modalButton.cloneNode(true);
      modalButton.parentNode.replaceChild(newModalButton, modalButton);
      this.elements.modalButton = newModalButton;

      newModalButton.addEventListener("click", function (event) {
        event.preventDefault();
        if (newModal) {
          document.dispatchEvent(new CustomEvent("open-measurement-modal-v2"));
        } else if (oldModal) {
          document.dispatchEvent(new CustomEvent("open-measurement-modal"));
        }
      });
    },

    // ── Option groups — div-based accordion ───────────────────────────────
    setupOptionGroups: function () {
      const { optionGroups, selectedOptionField } = this.elements;
      if (!optionGroups || optionGroups.length === 0) return;

      optionGroups.forEach((group) => {
        const btn = group.querySelector(".cm-option-item");
        if (!btn) return;

        // Make inputs required when measurements panel is open
        this.updateRequiredInputs(group);

        btn.addEventListener("click", (event) => {
          event.preventDefault();
          const isOpen = group.classList.contains("is-open");

          // Close all groups
          optionGroups.forEach((g) => {
            g.classList.remove("is-open");
            this.updateRequiredInputs(g);
          });

          // Open clicked group if it was closed
          if (!isOpen) {
            group.classList.add("is-open");
            this.updateRequiredInputs(group);
          }

          // Update hidden option field
          if (selectedOptionField) {
            const optionValue = group.getAttribute("data-cm-option");
            if (!isOpen) {
              switch (optionValue) {
                case "provide_now":
                  selectedOptionField.value = "";
                  selectedOptionField.setAttribute("disabled", "disabled");
                  break;
                case "email_later":
                  selectedOptionField.value = "Measurements Later";
                  selectedOptionField.removeAttribute("disabled");
                  break;
                case "measuring_kit":
                  selectedOptionField.value = "Measuring Kit";
                  selectedOptionField.removeAttribute("disabled");
                  break;
              }
            }
          }
        });
      });
    },

    updateRequiredInputs: function (group) {
      const isOpen = group.classList.contains("is-open");
      const hasMeasurementsPanel = group.querySelector(".cm-measurements-panel");
      if (!hasMeasurementsPanel) return;
      const inputs = group.querySelectorAll(".cm-custom-input");
      inputs.forEach((input) => { input.required = isOpen; });
    },

  };

  CustomMeasurements.init();
});
